"use client";

import { usePresence } from "@/hooks/usePresence";
import { cn } from "@/lib/utils";

interface OnlineIndicatorProps {
  userId: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  showText?: boolean;
  position?: "top-right" | "bottom-right" | "inline";
}

/**
 * OnlineIndicator component - shows a green dot if user is online
 * @param userId - The user ID to check online status for
 * @param className - Additional CSS classes
 * @param size - Size of the indicator dot (sm: 8px, md: 10px, lg: 12px)
 * @param showText - Whether to show "Online" text next to the dot
 * @param position - Position of the indicator (top-right, bottom-right for avatar overlay, inline for text)
 */
export function OnlineIndicator({
  userId,
  className,
  size = "md",
  showText = false,
  position = "inline",
}: OnlineIndicatorProps) {
  const { isUserOnline } = usePresence();

  const isOnline = isUserOnline(userId);

  if (!isOnline) return null;

  const sizeClasses = {
    sm: "h-2 w-2",
    md: "h-2.5 w-2.5",
    lg: "h-3 w-3",
  };

  const positionClasses = {
    "top-right": "absolute top-0 right-0 translate-x-1/4 -translate-y-1/4",
    "bottom-right": "absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4",
    "inline": "relative",
  };

  if (showText) {
    return (
      <div className={cn("flex items-center gap-1.5", className)}>
        <span
          className={cn(
            "rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-800",
            sizeClasses[size]
          )}
        />
        <span className="text-xs text-green-600 dark:text-green-400 font-medium">
          Online
        </span>
      </div>
    );
  }

  return (
    <span
      className={cn(
        "rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-800",
        sizeClasses[size],
        positionClasses[position],
        className
      )}
      title="Online"
    />
  );
}
